/**
 * Phase 4 slice 2 — Toolbox Talks. List + record (with attendee picker) + verify.
 */
import React, { useEffect, useState } from 'react';
import { Plus, CheckCircle2, Users, ShieldCheck } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import PhotoCaptureField from '../../components/safety/PhotoCaptureField';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';

import { formatDateTime as fmt } from '../../lib/format';

export default function ToolboxTalks() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [talks, setTalks] = useState([]);
  const [open, setOpen] = useState(false);
  const [roster, setRoster] = useState([]);
  const [form, setForm] = useState({ topic: '', duration_minutes: '', location_on_site: '', key_points: '', conducted_at: '' });
  const [picked, setPicked] = useState({});
  const [manual, setManual] = useState('');
  const [manualList, setManualList] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => {}); }, []);
  useEffect(() => { if (!projectId && projects.length) setProjectId(projects[0].id); }, [projects, projectId]);

  const load = () => { if (projectId) apiGet(`/safety/toolbox-talks?project_id=${projectId}`).then((d) => setTalks(d.toolbox_talks || [])).catch(() => {}); };
  useEffect(load, [projectId]); // eslint-disable-line

  const openForm = () => {
    setForm({ topic: '', duration_minutes: '', location_on_site: '', key_points: '', conducted_at: '' });
    setPicked({}); setManualList([]); setPhotos([]);
    if (projectId) apiGet(`/safety/project-attendees/${projectId}`).then((d) => setRoster(d.attendees || [])).catch(() => setRoster([]));
    setOpen(true);
  };

  const submit = async () => {
    if (!form.topic.trim()) { toast.error('Topic is required'); return; }
    const attendees = [
      ...roster.filter((r) => picked[r.employee_id]).map((r) => ({ employee_id: r.employee_id, employee_name: r.employee_name })),
      ...manualList.map((n, i) => ({ employee_id: `manual-${i}`, employee_name: n })),
    ];
    setSaving(true);
    try {
      await apiPost('/safety/toolbox-talks', {
        ...form, project_id: projectId, duration_minutes: Number(form.duration_minutes || 0),
        attendees, photos_base64: photos,
      });
      toast.success('Toolbox talk recorded');
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const verify = (t) => {
    apiPost(`/safety/toolbox-talks/${t.id}/verify`, {})
      .then(() => { toast.success('Verified'); load(); })
      .catch((e) => toast.error(e?.response?.status === 403 ? 'No permission to verify' : 'Failed'));
  };

  return (
    <div>
      <PageHeader overline="Safety / HSE" title="Toolbox Talks" subtitle="Daily pre-work safety briefings with attendance"
        actions={(
          <div className="flex items-center gap-3">
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="tt-project-select" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
            </select>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button onClick={openForm} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="tt-record-btn"><Plus size={14} /> Record</button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="tt-dialog">
                <DialogHeader><DialogTitle>Record Toolbox Talk</DialogTitle>
                  <DialogDescription>Log a pre-work safety briefing with its topic, attendees and photos.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="Topic (e.g. Working at height)" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="tt-topic" />
                  <div className="grid grid-cols-2 gap-3">
                    <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} placeholder="Duration (min)" className="px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="tt-duration" />
                    <input type="datetime-local" value={form.conducted_at} onChange={(e) => setForm({ ...form, conducted_at: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                  </div>
                  <input value={form.location_on_site} onChange={(e) => setForm({ ...form, location_on_site: e.target.value })} placeholder="Location on site" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                  <textarea value={form.key_points} onChange={(e) => setForm({ ...form, key_points: e.target.value })} rows={2} placeholder="Key points discussed" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />

                  <div>
                    <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><Users size={11} /> Attendees ({Object.values(picked).filter(Boolean).length + manualList.length})</div>
                    <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2 space-y-1" data-testid="tt-roster">
                      {roster.length === 0 ? <p className="text-xs text-slate-400">No roster for this project — add names manually below.</p>
                        : roster.map((r) => (
                          <label key={r.employee_id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={!!picked[r.employee_id]} onChange={(e) => setPicked({ ...picked, [r.employee_id]: e.target.checked })} />
                            {r.employee_name} <span className="text-[10px] text-slate-400">{r.role}</span>
                          </label>
                        ))}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <input value={manual} onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (manual.trim()) { setManualList([...manualList, manual.trim()]); setManual(''); } } }} placeholder="Add attendee by name" className="flex-1 px-2 py-1.5 border border-slate-300 rounded-sm text-sm" data-testid="tt-manual-input" />
                      <button type="button" onClick={() => { if (manual.trim()) { setManualList([...manualList, manual.trim()]); setManual(''); } }} className="px-3 py-1.5 bg-slate-100 rounded-sm"><Plus size={15} /></button>
                    </div>
                    {manualList.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{manualList.map((n, i) => <span key={i} className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded-sm">{n}</span>)}</div>}
                  </div>

                  <div>
                    <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Photos</div>
                    <PhotoCaptureField photos={photos} setPhotos={setPhotos} max={4} />
                  </div>
                </div>
                <DialogFooter>
                  <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm font-display font-bold rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e] disabled:opacity-60" data-testid="tt-submit">{saving ? 'Saving…' : 'Save Toolbox Talk'}</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table" data-testid="tt-table">
          <thead><tr><th>Topic</th><th>Conducted</th><th className="text-center">Attendees</th><th className="text-center">Signed</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {talks.length === 0 ? <tr><td colSpan={6} className="text-center text-sm text-slate-400 py-10">No toolbox talks recorded.</td></tr>
              : talks.map((t) => {
                const signed = (t.attendees || []).filter((a) => a.signed).length;
                return (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td><div className="font-display font-semibold text-[13px]">{t.topic}</div><div className="text-[11px] text-slate-500">{t.conducted_by_name} · {t.duration_minutes}min</div></td>
                    <td className="text-xs text-slate-600">{fmt(t.conducted_at)}</td>
                    <td className="text-center font-display font-bold">{(t.attendees || []).length}</td>
                    <td className="text-center text-slate-600">{signed}/{(t.attendees || []).length}</td>
                    <td><span className={`tag border ${t.status === 'verified' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{t.status}</span></td>
                    <td className="text-right">
                      {t.status !== 'verified' && <button onClick={() => verify(t)} className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:underline" data-testid={`tt-verify-${t.id}`}><ShieldCheck size={13} /> Verify</button>}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
